import { useState, useRef, useEffect } from "react";
import { useQuery } from "@tanstack/react-query";
import { X } from "lucide-react";
import { apiGet } from "@/lib/apiClient";
import type { ProfileSearchResponse } from "@/types/settings";
import type { ProfileObj } from "./ProfileLookup";

interface ProfileLookupMultiSelectProps {
  selectedProfiles: ProfileObj[];
  onChange: (profiles: ProfileObj[]) => void;
  disabled?: boolean;
  placeholder?: string;
  /** Debounce delay in ms before firing the search API (mirrors Blazor DebounceDelay=1000). */
  debounceDelay?: number;
}

/**
 * Multi-select profile lookup. Mirrors Blazor ProfileLookupMultiSelect component.
 * - Shows selected users as dark chips (matching .k-chip styling)
 * - Debounces search input
 * - Fires onChange(ProfileObj[]) whenever the selection changes
 */
export function ProfileLookupMultiSelect({
  selectedProfiles,
  onChange,
  disabled = false,
  placeholder = "Search and select users...",
  debounceDelay = 500
}: ProfileLookupMultiSelectProps) {
  const [inputValue, setInputValue] = useState("");
  const [debouncedTerm, setDebouncedTerm] = useState("");
  const [open, setOpen] = useState(false);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  const { data: results = [], isFetching } = useQuery({
    queryKey: ["profile-search-multi", debouncedTerm],
    queryFn: () =>
      apiGet<ProfileSearchResponse[]>(
        `ProfileSearch/searchDeloitteProfile?searchtext=${encodeURIComponent(debouncedTerm)}`
      ),
    enabled: debouncedTerm.trim().length >= 2,
    staleTime: 30_000
  });

  // Filter out already-selected profiles
  const filteredResults = results.filter(
    (r) => !selectedProfiles.some((s) => s.email.toLowerCase() === r.email.toLowerCase())
  );

  useEffect(() => {
    function handleClickOutside(e: MouseEvent) {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  function handleInputChange(e: React.ChangeEvent<HTMLInputElement>) {
    const val = e.target.value;
    setInputValue(val);
    if (debounceRef.current) clearTimeout(debounceRef.current);
    if (!val) { setDebouncedTerm(""); setOpen(false); return; }
    debounceRef.current = setTimeout(() => {
      setDebouncedTerm(val);
      setOpen(true);
    }, debounceDelay);
  }

  function handleSelect(profile: ProfileSearchResponse) {
    const fullName = profile.fullName || `${profile.firstName} ${profile.lastName}`.trim();
    const next: ProfileObj[] = [
      ...selectedProfiles,
      { fullName, email: profile.email, firstName: profile.firstName, lastName: profile.lastName }
    ];
    onChange(next);
    setInputValue("");
    setDebouncedTerm("");
    setOpen(false);
    inputRef.current?.focus();
  }

  function handleRemove(email: string) {
    onChange(selectedProfiles.filter((p) => p.email !== email));
  }

  return (
    <div
      ref={containerRef}
      style={{
        position: "relative",
        border: "0px solid #D0D0CE",
        minHeight: 32,
        background: disabled ? "linear-gradient(0deg, rgba(0,0,0,0.04), rgba(0,0,0,0.04)), #FFFFFF" : "#fff",
        padding: "2px 4px",
        display: "flex",
        flexWrap: "wrap",
        gap: 4,
        alignItems: "center",
        width: "100%",
        boxSizing: "border-box",
        cursor: disabled ? "not-allowed" : "text"
      }}
      onClick={() => { if (!disabled) inputRef.current?.focus(); }}
    >
      {/* Selected chips — matches .k-chip (#53565A background, white text, white X) */}
      {selectedProfiles.map((p) => (
        <span
          key={p.email}
          title={p.email}
          style={{
            display: "inline-flex",
            alignItems: "center",
            gap: 4,
            background: "#53565A",
            border: "1px solid #ddd",
            borderRadius: 0,
            padding: "2px 8px",
            fontSize: 12,
            color: "#FFFFFF",
            marginRight: 3,
            marginBottom: 3
          }}
        >
          {p.fullName || p.email}
          {!disabled && (
            <button
              type="button"
              aria-label={`Remove ${p.fullName || p.email}`}
              onMouseDown={(e) => { e.stopPropagation(); handleRemove(p.email); }}
              style={{ background: "none", border: "none", cursor: "pointer", padding: 0, lineHeight: 1, color: "#fff" }}
            >
              <X size={10} color="white" />
            </button>
          )}
        </span>
      ))}

      {!disabled && (
        <input
          ref={inputRef}
          type="text"
          value={inputValue}
          placeholder={selectedProfiles.length === 0 ? placeholder : ""}
          onChange={handleInputChange}
          onFocus={() => { if (debouncedTerm.trim().length >= 2) setOpen(true); }}
          style={{
            border: "none",
            outline: "none",
            fontSize: 13,
            minWidth: 120,
            flex: 1,
            background: "transparent"
          }}
        />
      )}

      {open && (isFetching || filteredResults.length > 0 || (debouncedTerm.length >= 2 && !isFetching && filteredResults.length === 0)) && (
        <ul
          role="listbox"
          style={{
            position: "absolute",
            top: "100%",
            left: 0,
            right: 0,
            zIndex: 100,
            background: "#fff",
            border: "1px solid #D0D0CE",
            maxHeight: 400,
            overflowY: "auto",
            margin: 0,
            padding: 0,
            listStyle: "none",
            scrollbarWidth: "thin",
            scrollbarColor: "#c1c1c1 #f5f5f5"
          }}
        >
          {isFetching && filteredResults.length === 0 && (
            <li style={{ padding: "8px 10px", fontSize: 12, color: "#63666A" }}>Searching&hellip;</li>
          )}
          {!isFetching && filteredResults.length === 0 && debouncedTerm.length >= 2 && (
            <li style={{ padding: "8px 10px", fontSize: 12, color: "#63666A" }}>No results found.</li>
          )}
          {filteredResults.map((p) => {
            const display = p.fullName || `${p.firstName} ${p.lastName}`.trim();
            return (
              <li
                key={p.email}
                role="option"
                aria-selected={false}
                onMouseDown={() => handleSelect(p)}
                className="row mb-3"
                style={{ padding: "6px 10px", cursor: "pointer", fontSize: 13, color: "#000", listStyle: "none" }}
                onMouseEnter={(e) => (e.currentTarget.style.background = "#E8E8E8")}
                onMouseLeave={(e) => (e.currentTarget.style.background = "")}
              >
                <span style={{ marginLeft: 1, display: "block", fontWeight: 400 }}>{display}</span>
                <small style={{ color: "#63666A" }}>{p.email}</small>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}
